import { useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { AcademieReport } from '../../types/academieReport';
import { BookOpen } from 'lucide-react';

interface Props {
  reports: AcademieReport[];
}

const PERIODS = [
  { id: '1m', label: '1 mois', months: 1 },
  { id: '3m', label: '3 mois', months: 3 },
  { id: '6m', label: '6 mois', months: 6 },
  { id: '1a', label: '1 an', months: 12 },
  { id: 'all', label: 'Tout', months: 0 },
];

const VIEWS = [
  { id: 'presence', label: 'Présence' },
  { id: 'taux', label: 'Taux (%)' },
];

export default function AcademieTrendChart({ reports }: Props) {
  const [selectedPeriod, setSelectedPeriod] = useState('3m');
  const [selectedView, setSelectedView] = useState('presence');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');

  const usingCustomRange = !!(startDate || endDate);

  const classNames = useMemo(() => {
    const names = [...new Set(reports.map(r => r.className).filter(Boolean))].sort();
    return names;
  }, [reports]);

  const chartData = useMemo(() => {
    let filtered = [...reports];
    if (selectedClass !== 'all') filtered = filtered.filter(r => r.className === selectedClass);
    if (usingCustomRange) {
      if (startDate) filtered = filtered.filter(r => r.date >= startDate);
      if (endDate) filtered = filtered.filter(r => r.date <= endDate);
    } else if (selectedPeriod !== 'all') {
      const p = PERIODS.find(p => p.id === selectedPeriod);
      if (p && p.months > 0) {
        const cut = new Date();
        cut.setMonth(cut.getMonth() - p.months);
        const cutStr = cut.toISOString().split('T')[0];
        filtered = filtered.filter(r => r.date >= cutStr);
      }
    }
    filtered.sort((a, b) => a.date.localeCompare(b.date));
    const byDate: Record<string, { inscrits: number; presents: number }> = {};
    filtered.forEach(r => {
      if (!byDate[r.date]) byDate[r.date] = { inscrits: 0, presents: 0 };
      byDate[r.date].inscrits += r.actualStudents || 0;
      byDate[r.date].presents += r.presentStudents || 0;
    });
    return Object.entries(byDate).map(([date, d]) => ({
      date: new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
      inscrits: d.inscrits,
      presents: d.presents,
      taux: d.inscrits > 0 ? Math.round((d.presents / d.inscrits) * 100) : 0,
    }));
  }, [reports, selectedPeriod, selectedClass, startDate, endDate, usingCustomRange]);

  const avgRate = useMemo(() => {
    if (!chartData.length) return 0;
    return Math.round(chartData.reduce((s, d) => s + d.taux, 0) / chartData.length);
  }, [chartData]);

  const totPresents = useMemo(() => chartData.reduce((s, d) => s + d.presents, 0), [chartData]);
  const totInscrits = useMemo(() => chartData.reduce((s, d) => s + d.inscrits, 0), [chartData]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="h-5 w-5 text-[#00665C]" />
        <h2 className="text-lg font-semibold text-gray-900">Évolution de la présence</h2>
      </div>

      {/* Résumé */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-[#00665C]">{totPresents}</div>
          <div className="text-xs text-gray-500">Total présents</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-blue-600">{totInscrits}</div>
          <div className="text-xs text-gray-500">Total inscrits</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className={`text-xl font-bold ${avgRate >= 75 ? 'text-green-600' : avgRate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>{avgRate}%</div>
          <div className="text-xs text-gray-500">Taux moyen</div>
        </div>
      </div>

      {/* Onglets vue + filtre classe */}
      <div className="flex flex-wrap gap-2 items-center mb-3">
        {VIEWS.map(v => (
          <button key={v.id} onClick={() => setSelectedView(v.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedView === v.id ? 'bg-[#00665C] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >{v.label}</button>
        ))}
        {classNames.length > 1 && (
          <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)}
            className="ml-auto px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
          >
            <option value="all">Toutes les classes</option>
            {classNames.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      {/* Filtres période + dates */}
      <div className="flex flex-wrap gap-2 items-end mb-4">
        <div className="flex gap-2 flex-wrap">
          {PERIODS.map(p => (
            <button key={p.id}
              onClick={() => { setSelectedPeriod(p.id); setStartDate(''); setEndDate(''); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                !usingCustomRange && selectedPeriod === p.id
                  ? 'bg-[#00665C] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >{p.label}</button>
          ))}
        </div>
        <div className="flex gap-2 items-end ml-auto">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Du</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Au</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
            />
          </div>
          {usingCustomRange && (
            <button onClick={() => { setStartDate(''); setEndDate(''); }}
              className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >Réinitialiser</button>
          )}
        </div>
      </div>

      {/* Graphique */}
      {chartData.length === 0 ? (
        <div className="h-[280px] flex items-center justify-center text-gray-400">
          <div className="text-center">
            <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>Aucune donnée pour cette période</p>
          </div>
        </div>
      ) : selectedView === 'presence' ? (
        <div className="h-[280px] md:h-[320px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="inscrits" name="Inscrits" stroke="#3B82F6" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
              <Line type="monotone" dataKey="presents" name="Présents" stroke="#00665C" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-[280px] md:h-[320px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v: number) => v + '%'} />
              <Tooltip formatter={(v: number) => v + '%'} />
              <Area type="monotone" dataKey="taux" name="Taux de présence" stroke="#00665C" fill="#00665C20" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}